import { storeSafetyNotificationIntent } from './safetyNotificationIntent';
import { OFFICE_NOTIFICATION_INTENT_KEY } from './officeNotificationIntent';
/**
 * iOS-native notifications (Apple only).
 * Local banners via ZiaNotifications plugin + optional APNs device token registration.
 * Notification tap → sessionStorage deep links into Water / Waste Management.
 */

import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isIosNativeAppRuntime } from './runtimeCapabilities';
import { hostWebApp } from './hostWebApp';
import { legacyAuthHeader } from '../utils/legacyHostCompat';
import { ZIA_OPEN_OPERATOR_DESK_APPROVALS_KEY } from '../../shared/operatorDeskApprovalsLink';
import { ZIA_OPEN_OPERATOR_DESK_KEY } from '../../shared/operatorDeskAttentionLink';

const LEGACY_USER_ID = legacyAuthHeader('User-Id');
const LEGACY_USERNAME = legacyAuthHeader('Username');

/** Custom DOM event name — VoiceNavigation / hubs listen for late opens. */
export const NOTIFICATION_OPEN_EVENT = 'zia:notification-open';

export type NotificationOpenPayload = {
  type?: string;
  event?: string;
  eventType?: string;
  orderId?: string;
  orderNumber?: string;
  jobId?: string;
  [key: string]: string | undefined;
};

interface ZiaNotificationsPlugin {
  available(): Promise<{ available: boolean; platform?: string }>;
  requestPermissions(): Promise<{ granted: boolean }>;
  getPermissionStatus(): Promise<{ status: string; granted: boolean }>;
  scheduleLocal(options: {
    title: string;
    body: string;
    id?: string;
    delaySeconds?: number;
    threadId?: string;
    category?: string;
    data?: Record<string, string>;
  }): Promise<{ id: string; scheduled: boolean }>;
  registerForRemote(): Promise<{ registering: boolean; deviceToken?: string | null }>;
  getDeviceToken(): Promise<{ deviceToken?: string | null }>;
  getLastNotificationOpen(): Promise<Record<string, string>>;
  addListener(
    eventName: 'deviceToken' | 'registrationError' | 'notificationOpened',
    listenerFunc: (event: Record<string, string>) => void,
  ): Promise<PluginListenerHandle>;
}

const ZiaNotifications = registerPlugin<ZiaNotificationsPlugin>('ZiaNotifications');

function authHeaders(): HeadersInit {
  let userId = hostWebApp.getUserId?.() || '';
  let username = hostWebApp.getUserName?.() || '';
  if (!userId) {
    userId = localStorage.getItem('dev-user-id') || '';
  }
  if (!username) {
    username = localStorage.getItem('dev-username') || 'field-user';
  }
  return {
    'Content-Type': 'application/json',
    'X-Telegram-User-Id': userId,
    'X-Telegram-Username': username,
    'X-Web-User-Id': userId,
    'X-Web-Username': username,
    [LEGACY_USER_ID]: userId,
    [LEGACY_USERNAME]: username,
  };
}

async function postDeviceToken(token: string): Promise<void> {
  if (!token || token.length < 32) return;
  try {
    await fetch('/api/ios-devices', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ device_token: token, platform: 'ios' }),
    });
  } catch (e) {
    console.warn('[iosNotifications] register token failed', e);
  }
}

let bootstrapped = false;
let tokenListener: PluginListenerHandle | null = null;
let openListener: PluginListenerHandle | null = null;
let openRoutingStarted = false;

/**
 * Write session keys + fire DOM event so VoiceNavigation can open the right hub.
 * Safe to call multiple times for the same payload.
 */
export function routeNotificationOpen(raw: NotificationOpenPayload | Record<string, string> | null | undefined): boolean {
  if (!raw || typeof raw !== 'object') return false;

  const type = String(raw.type || '').trim();
  if (!type) return false;

  try {
    if (type === 'worker_bulletin' || type === 'safety_desk' || (type === 'field_desk' && raw.recipientUserId)) {
      const intent = storeSafetyNotificationIntent(raw);
      if (!intent) return false;
      window.dispatchEvent(new CustomEvent(NOTIFICATION_OPEN_EVENT, { detail: intent }));
      return true;
    }
    if (type === 'notification_inbox') {
      sessionStorage.setItem('zia_open_notification_inbox', '1');
      window.dispatchEvent(new CustomEvent(NOTIFICATION_OPEN_EVENT, { detail: { type } }));
      return true;
    }
    if (['office_desk', 'company_desk', 'worker_training', 'worker_certs'].includes(type)) {
      const destination = type === 'worker_training' ? 'training' : type === 'worker_certs' ? 'certs'
        : String(raw.deskTab || '');
      if (type === 'office_desk' && destination !== 'payroll'
        || type === 'company_desk' && destination !== 'businessSeats') return false;
      sessionStorage.setItem(OFFICE_NOTIFICATION_INTENT_KEY, destination);
      window.dispatchEvent(new CustomEvent(NOTIFICATION_OPEN_EVENT, { detail: { type, deskTab: destination } }));
      return true;
    }
    if (type === 'haul_job') {
      const jobId = String(raw.jobId || '').trim();
      if (!jobId) return false;
      sessionStorage.setItem('zia_open_haul_job', jobId);
      window.dispatchEvent(new CustomEvent(NOTIFICATION_OPEN_EVENT, {detail:{type:'haul_job',jobId}}));
      return true;
    }

    if (type === 'operator_desk') {
      const deskTab = String(raw.deskTab || '').trim().toLowerCase();
      if (!['agreements', 'funding', 'payouts', 'requests', 'on-wells'].includes(deskTab)) return false;
      const workId = String(raw.workId || '').trim();
      if (deskTab === 'requests' && !workId) return false;
      sessionStorage.setItem(ZIA_OPEN_OPERATOR_DESK_KEY, JSON.stringify({
        tab: deskTab === 'requests' ? 'projects' : deskTab, ...(workId ? { workId } : {}),
      }));
      window.dispatchEvent(new CustomEvent(NOTIFICATION_OPEN_EVENT, {
        detail: { type, deskTab, event: raw.event || raw.eventType },
      }));
      return true;
    }

    if (type === 'operator_desk_approval') {
      const workId = String(raw.workId || '').trim();
      if (!workId) return false;
      sessionStorage.setItem(ZIA_OPEN_OPERATOR_DESK_APPROVALS_KEY, workId);
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: { type: 'operator_desk_approval', workId, event: raw.event || raw.eventType },
        }),
      );
      return true;
    }

    if (type === 'operator_desk_attention') {
      const workId = String(raw.workId || '').trim();
      if (!workId) return false;
      const requestedTab = String(raw.deskTab || raw.tab || 'attention').trim().toLowerCase() || 'attention';
      const deskTab = workId.startsWith('general:') && ['timeline', 'attention', 'requests'].includes(requestedTab) ? 'projects' : requestedTab;
      const kind = String(raw.kind || '').trim().toLowerCase();
      try {
        sessionStorage.setItem(
          ZIA_OPEN_OPERATOR_DESK_KEY,
          JSON.stringify({ workId, tab: deskTab, kind: kind || undefined }),
        );
      } catch { /* private mode */ }
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: {
            type: 'operator_desk_attention',
            workId,
            deskTab,
            kind,
            event: raw.event || raw.eventType,
          },
        }),
      );
      return true;
    }

    // Contractor Desk (owner: Job Master requests) / Field Desk (crew: assignments,
    // Job Master issued / denied). deskTab picks the toolbar destination.
    // Timesheet links open Field Desk → Timesheets (the Time Sheet lives there).
    if (type === 'fleet_desk' || type === 'contractor_desk' || type === 'field_desk' || type === 'timesheet' || type === 'time_entry') {
      const key = type === 'fleet_desk' ? 'zia_open_fleet_desk' : type === 'contractor_desk' ? 'zia_open_contractor_desk' : 'zia_open_field_desk';
      const deskTab = String(raw.deskTab || '').trim()
        || (type === 'timesheet' || type === 'time_entry' ? 'timesheets' : 'job-masters');
      sessionStorage.setItem(key, deskTab);
      if (type === 'contractor_desk' && raw.requestId) {
        sessionStorage.setItem('zia_open_contractor_request',String(raw.requestId));
      }
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: { type, deskTab, jobMasterId: String(raw.jobMasterId || ''), event: raw.event || raw.eventType },
        }),
      );
      return true;
    }

    if (type === 'water_delivery') {
      const orderId = String(raw.orderId || '').trim();
      const orderNumber = String(raw.orderNumber || '').trim();
      sessionStorage.setItem('zia_open_delivery_services', 'water');
      sessionStorage.setItem('zia_water_hub_tab', 'mine');
      if (orderId) sessionStorage.setItem('zia_water_open_order', orderId);
      if (orderNumber) sessionStorage.setItem('zia_water_open_order_number', orderNumber);
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: { type: 'water_delivery', orderId, orderNumber, event: raw.event || raw.eventType },
        }),
      );
      return true;
    }

    if (type === 'funded_bid') {
      const bidUuid = String(raw.bidUuid || '').trim();
      sessionStorage.setItem('zia_open_service_bids', '1');
      if (bidUuid) sessionStorage.setItem('zia_open_bid_uuid', bidUuid);
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: { type: 'funded_bid', bidUuid, event: raw.event || raw.eventType },
        }),
      );
      return true;
    }

    if (type === 'waste_management') {
      const jobId = String(raw.jobId || '').trim();
      sessionStorage.setItem('zia_open_waste_management', '1');
      if (jobId) sessionStorage.setItem('zia_wm_open_job', jobId);
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: { type: 'waste_management', jobId, event: raw.event || raw.eventType },
        }),
      );
      return true;
    }

    if (type === 'receipts') {
      sessionStorage.setItem('zia_open_receipts', '1');
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: { type: 'receipts', receiptId: String(raw.receiptId || ''), event: raw.event || raw.eventType },
        }),
      );
      return true;
    }

    // ── Amigos / Friends ─────────────────────────────────────────────────
    if (type.startsWith('friend_')) {
      // friend_request → Requests · friend_accepted → list ·
      // friend_location → map · friend_live_share → live-share prompt
      const tab = type === 'friend_request'
        ? 'requests'
        : type === 'friend_location'
          ? 'map'
          : type === 'friend_live_share'
            ? 'live'
            : 'friends';
      sessionStorage.setItem('zia_open_friends', tab);
      // Second key survives VoiceNavigation consuming the intent — the panel
      // reads it to land on the right sub-tab.
      sessionStorage.setItem('zia_friends_open_tab', tab);
      const friendUserId = String(raw.friendUserId || raw.fromUserId || '').trim();
      if (friendUserId) sessionStorage.setItem('zia_friends_focus_user', friendUserId);
      const sessionId = String(raw.sessionId || '').trim();
      if (sessionId) sessionStorage.setItem('zia_friends_live_share_session', sessionId);
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: { type, tab, friendUserId, sessionId, event: raw.event || raw.eventType },
        }),
      );
      return true;
    }

    // ── My Crews ─────────────────────────────────────────────────────────
    if (type.startsWith('crew_')) {
      sessionStorage.setItem('zia_open_crews', '1');
      const crewId = String(raw.crewId || '').trim();
      if (crewId) sessionStorage.setItem('zia_crews_open_crew', crewId);
      // crew_member → members · crew_meeting → meetings · crew_order → orders ·
      // crew_weather → weather banner · crew_dm → DM thread
      const section = type.replace('crew_', '');
      sessionStorage.setItem('zia_crews_open_section', section);
      const fromUsername = String(raw.fromUsername || '').trim();
      if (type === 'crew_dm' && fromUsername) {
        sessionStorage.setItem('zia_crews_open_dm_user', fromUsername);
      }
      window.dispatchEvent(
        new CustomEvent(NOTIFICATION_OPEN_EVENT, {
          detail: { type, crewId, section, fromUsername, event: raw.event || raw.eventType },
        }),
      );
      return true;
    }
  } catch (e) {
    console.warn('[iosNotifications] route open failed', e);
  }
  return false;
}

export const iosNotifications = {
  isIos(): boolean {
    return isIosNativeAppRuntime();
  },

  async available(): Promise<boolean> {
    if (!isIosNativeAppRuntime()) return false;
    try {
      const r = await ZiaNotifications.available();
      return Boolean(r?.available);
    } catch {
      return false;
    }
  },

  /**
   * Request notification permission, register for APNs, upload token.
   * Safe to call multiple times.
   */
  async bootstrap(): Promise<{ granted: boolean }> {
    if (!isIosNativeAppRuntime()) return { granted: false };
    try {
      const perm = await ZiaNotifications.requestPermissions();
      if (!perm.granted) return { granted: false };

      if (!tokenListener) {
        tokenListener = await ZiaNotifications.addListener('deviceToken', (ev) => {
          if (ev.deviceToken) void postDeviceToken(ev.deviceToken);
        });
      }

      const reg = await ZiaNotifications.registerForRemote();
      if (reg.deviceToken) await postDeviceToken(reg.deviceToken);
      else {
        const existing = await ZiaNotifications.getDeviceToken();
        if (existing.deviceToken) await postDeviceToken(existing.deviceToken);
      }

      bootstrapped = true;
      return { granted: true };
    } catch (e) {
      console.warn('[iosNotifications] bootstrap failed', e);
      return { granted: false };
    }
  },

  /**
   * Listen for notification taps (local + remote) and route into Water / WM.
   * Also drains cold-start pending open from native.
   */
  async startOpenRouting(): Promise<void> {
    if (!isIosNativeAppRuntime() || openRoutingStarted) return;
    openRoutingStarted = true;

    try {
      if (!openListener) {
        openListener = await ZiaNotifications.addListener('notificationOpened', (ev) => {
          routeNotificationOpen(ev);
        });
      }
      const last = await ZiaNotifications.getLastNotificationOpen();
      if (last && Object.keys(last).length > 0) {
        routeNotificationOpen(last);
      }
    } catch (e) {
      console.warn('[iosNotifications] open routing failed', e);
      openRoutingStarted = false;
    }
  },

  /** Immediate Lock Screen / banner via UNUserNotificationCenter. */
  async scheduleLocal(opts: {
    title: string;
    body: string;
    id?: string;
    data?: Record<string, string>;
  }): Promise<boolean> {
    if (!isIosNativeAppRuntime()) return false;
    try {
      if (!bootstrapped) await this.bootstrap();
      const type = opts.data?.type || '';
      const isWm = type === 'waste_management';
      const isFriends = type.startsWith('friend_');
      const isCrews = type.startsWith('crew_');
      await ZiaNotifications.scheduleLocal({
        title: opts.title,
        body: opts.body,
        id: opts.id,
        delaySeconds: 0.2,
        threadId: isFriends ? 'friends' : isCrews ? 'crews' : isWm ? 'waste-management' : 'water-delivery',
        category: isFriends ? 'FRIENDS' : isCrews ? 'CREWS' : isWm ? 'WASTE_MANAGEMENT' : 'WATER_DELIVERY',
        data: opts.data,
      });
      return true;
    } catch (e) {
      console.warn('[iosNotifications] scheduleLocal failed', e);
      return false;
    }
  },
};
